import {
  Form,
  Formik,
  type FormikErrors,
  type FormikHelpers,
  type FormikProps,
  type FormikValues,
} from "formik";
import React, {
  type ComponentPropsWithoutRef,
  useEffect,
  useState,
} from "react";
import * as Yup from "yup";
import { type AnyObject as yupAnyObject } from "yup/lib/types";
import PillarFormCheckbox from "shared/components/pillar-form/components/PillarFormCheckbox";
import PillarFormNumberInput from "shared/components/pillar-form/components/PillarFormNumberInput";
import PillarFormSelectMenu from "shared/components/pillar-form/components/PillarFormSelectMenu";
import PillarFormSwitch from "shared/components/pillar-form/components/PillarFormSwitch";
import PillarFormTextAreaInput from "shared/components/pillar-form/components/PillarFormTextAreaInput";
import PillarFormTextInput from "shared/components/pillar-form/components/PillarFormTextInput";
import type PillarFormTypes from "shared/components/pillar-form/components/PillarFormTypes";
import PillarFormUSDInput from "shared/components/pillar-form/components/PillarFormUSDInput";
import { populatePillarFormComponents } from "shared/components/pillar-form/components/populatePillarFormComponents";
import { flattenChildComponents } from "shared/helpers/react/flatten-child-components";

type PillarFormProps<GenericFormikValues> = ComponentPropsWithoutRef<"form"> & {
  children: React.ReactNode;
  handleSubmit: (
    values: GenericFormikValues,
    formikHelpers?: FormikHelpers<GenericFormikValues>,
  ) => Promise<void>;
  handleChanged?: (values: GenericFormikValues) => void;
  validateOnChange?: boolean;
  overrideInitialValues?: Partial<GenericFormikValues> | null;
  yupValidation?: yupAnyObject;
  testid?: string;
  /**
   * @deprecated Use `testid` instead
   */
  ["data-testid"]?: string;
};

const PillarFormToBeForwarded = <GenericFormikValues extends FormikValues>(
  {
    children,
    handleSubmit,
    handleChanged,
    overrideInitialValues,
    validateOnChange = false,
    yupValidation,
    testid,
    ["data-testid"]: legacyTestId,
    ...props
  }: PillarFormProps<GenericFormikValues>,
  ref: React.ForwardedRef<FormikProps<GenericFormikValues> | undefined>,
) => {
  if (!testid && legacyTestId) {
    testid = legacyTestId;
  }
  const [formValues, setFormValues] = useState<
    GenericFormikValues | undefined
  >();
  const [validationErrors, setValidationErrors] = useState<
    FormikErrors<GenericFormikValues> | undefined
  >();

  useEffect(() => {
    if (!formValues || !handleChanged) {
      return;
    }
    handleChanged(formValues);
  }, [formValues]);

  useEffect(() => {
    if (overrideInitialValues) {
      setFormValues(overrideInitialValues as GenericFormikValues);
    }
  }, [overrideInitialValues]);

  const initialValues: GenericFormikValues = (overrideInitialValues ??
    {}) as GenericFormikValues;
  const flattenedChildren = flattenChildComponents(children);
  const yupSchema = yupValidation ?? Yup.object({});

  flattenedChildren.forEach((child) => {
    if (!overrideInitialValues) {
      if (
        child.type === PillarFormTextInput ||
        child.type === PillarFormTextAreaInput ||
        child.type === PillarFormNumberInput ||
        child.type === PillarFormUSDInput
      ) {
        initialValues[child.props.name as keyof GenericFormikValues] =
          child.props.initalValue ?? "";
      }
      if (
        child.type === PillarFormSwitch ||
        child.type === PillarFormCheckbox
      ) {
        initialValues[child.props.name as keyof GenericFormikValues] =
          child.props.initalValue ?? false;
      }
      if (child.type === PillarFormSelectMenu) {
        initialValues[child.props.name as keyof GenericFormikValues] =
          child.props.initialOption?.value ?? undefined;
      }
    }
  });

  // Custom validate function to log validation errors
  const validate = (values: GenericFormikValues) => {
    try {
      yupSchema.validateSync(values, { abortEarly: false });
    } catch (error: any) {
      if (process.env.NODE_ENV !== "production") {
        console.log("Validation error for values:", values);
      }
      if (
        error instanceof Yup.ValidationError ||
        ("inner" in error && error.inner)
      ) {
        // Transform Yup errors to Formik-compatible error object
        const formikErrors = error.inner.reduce(
          (acc: { [key: string]: string }, err: Yup.ValidationError) => {
            if (process.env.NODE_ENV !== "production") {
              console.log(
                `Validation error for field ${err.path}: ${err.message}`,
              );
            }
            if (err.path) {
              acc[err.path] = acc[err.path]
                ? `${acc[err.path]}, ${err.message}`
                : err.message;
            }
            return acc;
          },
          {},
        );
        return formikErrors;
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (
        values: GenericFormikValues,
        formikHelpers: FormikHelpers<GenericFormikValues>,
      ) => {
        try {
          await handleSubmit(values, formikHelpers);
        } catch (error: any) {
          formikHelpers.setStatus({ error: error.message });
        }
      }}
      validateOnChange={validateOnChange}
      enableReinitialize={true}
      innerRef={(formikActions) => {
        if (formikActions) {
          setFormValues(formikActions.values);
          if (formikActions.isSubmitting && !formikActions.isValidating) {
            const errorFieldsArray = Object.keys(formikActions.errors);
            if (
              !errorFieldsArray.every(
                (errorFieldName) => !!formikActions.touched[errorFieldName],
              )
            ) {
              errorFieldsArray.map((fieldName: string) =>
                formikActions.setFieldTouched(fieldName),
              );
            }
          }
        }
        if (ref) {
          if (typeof ref === "function") {
            ref(formikActions);
          } else {
            ref.current = formikActions;
          }
        }
      }}
      validationSchema={yupSchema}
      validate={validate} // Add the custom validate function
    >
      <Form {...props} data-testid={testid}>
        {children}
      </Form>
    </Formik>
  );
};

type PillarFormType = {
  <GenericFormikValues extends FormikValues>(
    props: PillarFormProps<GenericFormikValues> &
      React.RefAttributes<FormikProps<GenericFormikValues>>,
  ): React.ReactElement;
} & typeof PillarFormTypes;

const PillarForm = React.forwardRef(
  PillarFormToBeForwarded,
) as unknown as PillarFormType;

populatePillarFormComponents(PillarForm);

export default PillarForm;
